export const ACCOUNT_ID_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/;
export const PUBLIC_EQUITY_ALL_HISTORY_FROM = 946_684_800;
export const PUBLIC_EQUITY_SERIES_LIMIT = 5_000;

/** Canonical public strategy labels shared by the website and report domains. */
export const strategyNames: Readonly<Record<string, string>> = Object.freeze({
  "7275": "Amazing System 7275",
  "UNMAPPED/OTHER": "UNMAPPED／OTHER",
});

export function strategyDisplayName(magic: string): string {
  return strategyNames[magic] ?? `Magic ${magic}`;
}

export type AccountType = "REAL" | "DEMO";
export type PositionSide = "BUY" | "SELL";
export type DataFreshness = "LIVE" | "DELAYED" | "OFFLINE";
export type RiskStatus = "NORMAL" | "CAUTION" | "ELEVATED" | "OFFLINE";
export type BackfillStatus = "COMPLETE" | "DEALS_PENDING_REBUILD" | "NOT_STARTED";
export type PerformanceDataSource =
  | "HISTORICAL_BALANCE"
  | "LIVE_EQUITY"
  | "MIXED"
  | "N/A";
export type PerformancePointSource = Exclude<PerformanceDataSource, "MIXED" | "N/A">;
export type MonthlyPerformanceStatus =
  | "COMPLETE"
  | "MTD"
  | "PARTIAL_INCEPTION"
  | "INCOMPLETE_BASELINE"
  | "INCOMPLETE_ENDING"
  | "NO_PERFORMANCE_POINTS"
  | "NO_TRADES"
  | "SOURCE_TRANSITION"
  | "DATA_GAP";
export type DealType =
  | "BUY"
  | "SELL"
  | "BALANCE"
  | "CREDIT"
  | "CHARGE"
  | "CORRECTION"
  | "BONUS"
  | "COMMISSION"
  | "INTEREST"
  | "DIVIDEND"
  | "TAX"
  | "OTHER";
export type DealEntry = "IN" | "OUT" | "INOUT" | "OUT_BY" | "N/A";

export interface PositionInput {
  position_ticket: string;
  magic?: string | null;
  symbol: string;
  side: PositionSide;
  volume: number;
  open_price: number;
  current_price: number;
  profit: number;
  /** Raw MT5 broker/market timestamp retained for auditability. */
  opened_at: number;
  /** UTC-normalized entry time supplied by the Publisher for presentation. */
  opened_at_utc?: number | null;
}

export interface SnapshotInput {
  schema_version: 1;
  account_id: string;
  account_type: AccountType;
  display_name?: string;
  currency: string;
  recorded_at: number;
  balance: number;
  equity: number;
  margin: number;
  free_margin: number;
  floating_profit: number;
  cash_flow_total: number;
  /** Runtime broker-server offset used to normalize position timestamps. */
  server_utc_offset_seconds?: number;
  signalstart_url?: string | null;
  positions?: PositionInput[];
}

export interface DealInput {
  ticket: string;
  /** Raw MT5 broker/market timestamp. Do not use this as a UTC notification window timestamp. */
  occurred_at: number;
  type: DealType;
  entry: DealEntry;
  symbol?: string | null;
  volume: number;
  price: number;
  profit: number;
  commission: number;
  swap: number;
  fee: number;
  magic?: string | null;
  position_id?: string | null;
}

export interface DealsInput {
  schema_version: 1;
  account_id: string;
  deals: DealInput[];
}

export interface DealsIngestInput extends DealsInput {
  /** Runtime broker-server offset from UTC, recalculated by the Publisher for each upload batch. */
  server_utc_offset_seconds: number;
}

export interface RebuildHistoryInput {
  account_id: string;
}

export type CanonicalItdCashFlowSource =
  | "ACCOUNT_CURRENT_CASH_FLOW_TOTAL"
  | "LIVE_EQUITY_HISTORY_CASH_FLOW_TOTAL"
  | "HISTORICAL_BALANCE_CASH_FLOW"
  | "N/A";

export interface CanonicalItdCashFlowMetrics {
  source: CanonicalItdCashFlowSource;
  asOf: number | null;
  balance: number | null;
  netCashFlow: number | null;
  cumulativeRealizedNetPnl: number | null;
  cumulativeCashFlowNeutralReturnPct: number | null;
  unavailableReason:
    | "BALANCE_UNAVAILABLE"
    | "NET_CASH_FLOW_UNAVAILABLE"
    | "NON_POSITIVE_NET_CASH_FLOW"
    | null;
}

export interface PublicSummary {
  accountId: string;
  displayName: string;
  accountType: AccountType;
  currency: string;
  signalStartUrl: string | null;
  balance: number;
  equity: number;
  floatingProfit: number;
  margin: number;
  freeMargin: number;
  peakEquity: number;
  /** Balance/net-cash-flow ITD metrics; distinct from performance-index return. */
  itdCashFlow: CanonicalItdCashFlowMetrics;
  cumulativeReturnPct: number;
  monthlyReturnPct: number | null;
  currentDrawdownPct: number;
  maximumDrawdownPct: number;
  lastUpdatedAt: number;
  liveStartAt: number | null;
  historyStartAt: number | null;
  historyEndAt: number | null;
  historyBackfillStatus: BackfillStatus;
  dealCount: number;
  dataVersion: "v5" | "v5.1" | "v5.4";
}

export interface EquityPoint {
  recordedAt: number;
  balance: number;
  equity: number;
  returnPct: number;
  drawdownPct: number;
}

export interface HistoricalBalancePoint {
  recordedAt: number;
  balance: number;
  cashFlow: number;
  tradingPnl: number;
  returnPct: number;
  /** Optional gross breakdown retained when deposits and withdrawals share a UTC day. */
  cashIn?: number;
  cashOut?: number;
  cashFlowTimes?: number[];
}

export interface LiveBalancePoint {
  recordedAt: number;
  balance: number;
}

export interface LiveEquityPoint {
  recordedAt: number;
  equity: number;
  returnPct: number;
  drawdownPct: number;
}

export interface EquitySeries<TPoint, TType extends string, TSource extends string> {
  type: TType;
  source: TSource;
  startAt: number | null;
  endAt: number | null;
  points: TPoint[];
}

export interface PublicEquity {
  accountId: string;
  currency: string;
  from: number;
  to: number;
  historical_balance: EquitySeries<
    HistoricalBalancePoint,
    "HISTORICAL_BALANCE",
    "DEAL_RECONSTRUCTED"
  >;
  live_balance: EquitySeries<LiveBalancePoint, "LIVE_BALANCE", "SNAPSHOT">;
  live_equity: EquitySeries<LiveEquityPoint, "LIVE_EQUITY", "SNAPSHOT">;
  firstActualTradeAt: number | null;
  /** Stable deal-order identity used when funding and trading share a timestamp. */
  firstActualTradeTicket?: string | null;
  initialBalance: InitialBalanceBaseline | null;
  /** Machine-readable diagnostic when no reliable initial Balance can be resolved. */
  initialBalanceReason?: InitialBalanceBaselineFailureReason | null;
  pnl: AccountPnlReconciliation;
  /** Backward-compatible alias for clients using the original live response. */
  points: EquityPoint[];
}

export interface InitialBalanceBaseline {
  recordedAt: number;
  balance: number;
  performanceIndex: number;
  source:
    | "HISTORICAL_PRE_TRADE_POINT"
    | "DEAL_LEDGER_PRE_TRADE"
    | "DEAL_RECONSTRUCTED_PRE_TRADE";
}

export type InitialBalanceBaselineFailureReason =
  | "NO_ACTUAL_TRADE_DEAL"
  | "NON_FINITE_CURRENT_BALANCE"
  | "INVALID_DEAL_ORDER_KEY"
  | "NON_FINITE_DEAL_IMPACT"
  | "LEDGER_RECONCILIATION_FAILED"
  | "NON_FINITE_PRE_TRADE_BALANCE"
  | "NON_POSITIVE_PRE_TRADE_BALANCE";

export interface AccountPnlReconciliation {
  cumulativeCashIn: number;
  cumulativeCashOut: number;
  netCashFlow: number;
  realizedTradingPnl: number;
  unrealizedPnl: number;
  totalTradingPnlIncludingFloating: number;
  balance: number;
  equity: number;
  dealBasedNetCashFlow: number | null;
  dealBasedRealizedTradingPnl: number | null;
  cashFlowDifference: number | null;
  realizedDifference: number | null;
  floatingDifference: number;
  status: "MATCHED" | "RECONCILED_TO_ACCOUNT_CURRENT" | "INSUFFICIENT_DEALS";
}

export interface MonthlyPerformance {
  month: string;
  returnPct: number | null;
  maxDrawdownPct: number | null;
  tradeCount: number;
  source: PerformanceDataSource;
  status: MonthlyPerformanceStatus;
  reason: string | null;
  startAt: number | null;
  endAt: number | null;
  baselineAt: number | null;
  baselineSource: PerformancePointSource | null;
  isComplete: boolean;
  trackingStartAt?: number | null;
}

export interface CanonicalBalanceSeriesPoint {
  recordedAt: number;
  balance: number;
  source: "HISTORICAL_BALANCE" | "LIVE_BALANCE";
}

export interface CanonicalMtdStrategyMetric {
  key: string;
  magic: string | null;
  displayName: string;
  month: MonthlyPerformance;
  performanceMeaning: "STRATEGY_CONTRIBUTION_VS_ACCOUNT_CAPITAL";
  realizedNetPnl: number;
}

export interface CanonicalItdSummaryMetrics {
  source: "CANONICAL_METRIC_OWNERS";
  dataAvailable: boolean;
  unavailableReason: "SUMMARY_UNAVAILABLE" | "SUMMARY_AFTER_CUTOFF" | null;
  periodStart: number | null;
  periodEnd: number;
  asOf: number | null;
  cashFlow: CanonicalItdCashFlowMetrics;
  currentDrawdownPct: number | null;
  maximumDrawdownPct: number | null;
  balance: number | null;
  equity: number | null;
}

export interface CanonicalMtdMetrics {
  accountId: string;
  currency: string;
  cutoffAt: number;
  timezone: "Asia/Taipei";
  periodStart: number;
  monthKey: string;
  itd: CanonicalItdSummaryMetrics;
  currentMonth: MonthlyPerformance;
  monthlyHistory: MonthlyPerformance[];
  strategies: CanonicalMtdStrategyMetric[];
  balancePoints: CanonicalBalanceSeriesPoint[];
  sourceBalancePointCount: number;
  realizedNetPnl: number;
  sourceDealLimitReached: boolean;
  firstActualTradeAt: number | null;
}

export interface MonthlyOverview {
  bestMonth: MonthlyPerformance | null;
  worstMonth: MonthlyPerformance | null;
  positiveMonthRatioPct: number | null;
  longestPositiveStreak: number;
  longestNegativeStreak: number;
  completeMonthCount: number;
  positiveMonthCount: number;
}

export interface TradeSegmentStats {
  key: string;
  trades: number;
  netPnl: number;
  winRatePct: number | null;
  profitFactor: number | null;
  expectancy: number | null;
  averageWin: number | null;
  averageLoss: number | null;
  averagePayoffRatio: number | null;
  /** Drawdown of cumulative closed-trade P/L relative to the account's pre-trade capital. */
  maximumDrawdownContributionPct: number | null;
}

export interface TradeStatistics {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  netPnl: number;
  winRatePct: number | null;
  profitFactor: number | null;
  averageWin: number | null;
  averageLoss: number | null;
  averagePayoffRatio: number | null;
  expectancy: number | null;
  recoveryFactor: number | null;
  sharpeRatio: number | null;
  sortinoRatio: number | null;
  maxWinningStreak: number;
  maxLosingStreak: number;
  averageHoldingSeconds: number | null;
  dataSufficient: boolean;
  notes: string[];
}

export interface DrawdownPoint {
  recordedAt: number;
  drawdownPct: number;
  source: Exclude<PerformanceDataSource, "MIXED" | "N/A">;
}

export interface DrawdownAnalytics {
  points: DrawdownPoint[];
  currentDrawdownPct: number | null;
  maximumDrawdownPct: number | null;
  longestDrawdownDays: number | null;
  averageRecoveryDays: number | null;
  maxDailyLossPct: number | null;
  maxDailyGainPct: number | null;
  riskStatus: RiskStatus;
}

/** Canonical owner for inception-to-date maximum drawdown in every presentation. */
export function selectItdMaximumDrawdownPct(
  drawdown: Pick<DrawdownAnalytics, "maximumDrawdownPct"> | null | undefined,
): number | null {
  return drawdown?.maximumDrawdownPct ?? null;
}

/** Canonical owner for inception-to-date cash-flow-neutral return in every presentation. */
export function selectItdCashFlowNeutralReturnPct(
  metrics: Pick<CanonicalItdCashFlowMetrics, "cumulativeCashFlowNeutralReturnPct"> | null | undefined,
): number | null {
  return metrics?.cumulativeCashFlowNeutralReturnPct ?? null;
}

/** Canonical owner for inception-to-date realized net P/L in every presentation. */
export function selectItdCumulativeRealizedNetPnl(
  metrics: Pick<CanonicalItdCashFlowMetrics, "cumulativeRealizedNetPnl"> | null | undefined,
): number | null {
  return metrics?.cumulativeRealizedNetPnl ?? null;
}

export interface PublicAnalytics {
  accountId: string;
  currency: string;
  balance: number;
  equity: number;
  generatedAt: number;
  dataVersion: "v5.4";
  performanceStartAt: number | null;
  drawdownScope: "HISTORICAL_BALANCE_AND_LIVE_EQUITY";
  cumulativeReturnPct: number | null;
  currentMonthReturnPct: number | null;
  currentDrawdownPct: number | null;
  maximumDrawdownPct: number | null;
  historyStartAt: number | null;
  liveStartAt: number | null;
  monthly: MonthlyPerformance[];
  monthlyOverview: MonthlyOverview;
  trades: TradeStatistics;
  longShort: TradeSegmentStats[];
  bySymbol: TradeSegmentStats[];
  byMagic: TradeSegmentStats[];
  drawdown: DrawdownAnalytics;
  firstActualTradeAt: number | null;
  /** Stable deal-order identity used when funding and trading share a timestamp. */
  firstActualTradeTicket?: string | null;
  initialBalance?: InitialBalanceBaseline | null;
  initialBalanceReason?: InitialBalanceBaselineFailureReason | null;
  scope: AnalyticsScope;
  bestTrade: PublicTradeRecord | null;
  worstTrade: PublicTradeRecord | null;
  strategyContribution: StrategyContributionAnalytics | null;
  availableStrategies: string[];
  sourceDealCount: number;
  unmatchedDealCount: number;
  sourceDealLimitReached: boolean;
}

export interface AnalyticsScope {
  type: "ACCOUNT" | "STRATEGY";
  strategy: "ALL" | string;
  label: string;
  performanceMeaning: "ACCOUNT_CASH_FLOW_NEUTRAL_RETURN" | "STRATEGY_CONTRIBUTION_VS_ACCOUNT_CAPITAL";
  isRealAccountBalance: boolean;
}

export interface StrategyContributionPoint {
  recordedAt: number;
  cumulativePnl: number;
  contributionPct: number | null;
}

export interface StrategyContributionAnalytics {
  strategy: string;
  baselineBalance: number | null;
  baselineAt: number | null;
  cumulativePnl: number;
  maximumSimulatedDrawdownPct: number | null;
  points: StrategyContributionPoint[];
}

export interface PublicTradeRecord {
  ticket?: string;
  openedAt: number | null;
  closedAt: number;
  symbol: string;
  side: PositionSide;
  volume: number;
  entryPrice: number | null;
  exitPrice: number;
  profit: number;
  commission: number;
  swap: number;
  fee: number;
  netPnl: number;
  holdingSeconds: number | null;
  magic: string | null;
  closeReason?: "OUT" | "OUT_BY" | "INOUT";
}

export interface PublicTrades {
  accountId: string;
  currency: string;
  total: number;
  limit: number;
  offset: number;
  sourceLimitReached: boolean;
  unmatchedDealCount: number;
  items: PublicTradeRecord[];
  filters: {
    symbols: string[];
    magicNumbers: string[];
    months: string[];
    closeReasons?: ("OUT" | "OUT_BY" | "INOUT")[];
  };
}

export interface ApiEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string[];
  };
}
